import { h, RefObject } from 'preact';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'preact/hooks';
import { changes } from '../subs';
import { Change, CSSAttribute } from '../types';
import { useComponentLoaded } from './hooks/useComponentsLoaded';
import { useResizeObserver } from './hooks/useResizeObserver';
import { Keywords } from './hooks/useTextKeywords';
import { teads_tag } from './theme';

export interface LineClampProps {
  lines: number;
  text: string;
  dots: boolean;
  style?: CSSAttribute;
  styleWord?: (str: string) => CSSAttribute;
  prefix?: HTMLElement;
  keywords?: boolean;
  containerRef?: RefObject<HTMLDivElement>;
  className?: string;
}

function textNodes(parent: HTMLElement): Node[] {
  const walker = document.createTreeWalker(parent, NodeFilter.SHOW_TEXT, () => NodeFilter.FILTER_ACCEPT);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  return nodes;
}

function replaceElement(parent: HTMLElement, replace: Node, children: HTMLElement[]) {
  if (!parent) {
    console.error('replaceElement with no parent');
    return;
  }
  const frag = document.createDocumentFragment();
  children.forEach((child) => frag.appendChild(child));
  parent.insertBefore(frag, replace);
  if (replace) parent.removeChild(replace);
}

const partsSplitWithoutKeywordsRegex = /(\s+)/;
const partsSplitWithKeywordsRegex = new RegExp(`((?:\\/Keyword \\[(?:${Keywords.join('|')})])|(?:\\s+))`);

const keywordRegex = new RegExp(`^\\/Keyword (\\[(?:${Keywords.join('|')})])$`);
const keywordStyle = { color: teads_tag };

function insertText(
  element: HTMLElement,
  text: string,
  prefixElement: HTMLElement | undefined,
  styleWord: { (word: string): CSSAttribute } | null,
  keywords: boolean = false
) {
  const createSpan = (text: string, extraStyle?: CSSAttribute) => {
    const elem = document.createElement('span');
    Object.assign(elem.style, styleWord && styleWord(text), extraStyle);
    elem.appendChild(document.createTextNode(text));
    return elem;
  };

  element.innerHTML =
    text
      ?.replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;')
      .replace(/\n/g, '<br>')
      .replace(/\u00a0/g, '\u0020') ?? ''; // NBSP -> Space

  const partsSplitRegex = keywords ? partsSplitWithKeywordsRegex : partsSplitWithoutKeywordsRegex;
  const nodes = textNodes(element);
  for (const node of nodes) {
    const parent = node.parentNode as HTMLElement;
    const text = node.textContent || '';
    const parts = text
      .split(partsSplitRegex)
      .filter((_) => _.length !== 0)
      .reduce<HTMLSpanElement[]>((result, part) => {
        if (keywords) {
          const match = part.match(keywordRegex);

          if (match) {
            result.push(createSpan(match[1], keywordStyle));
          } else {
            result.push(createSpan(part));
          }
        } else {
          result.push(createSpan(part));
        }

        return result;
      }, []);
    replaceElement(parent, node, parts);
  }
  prefixElement && element.insertBefore(prefixElement, element.firstChild);
}

function getLineClampStyle(lines: number, lineHeight: number, dots: boolean, style: object | undefined) {
  return {
    ...style,
    overflow: 'hidden',
    'max-height': `${lines * lineHeight}px`,
    ...(dots && {
      display: '-webkit-box',
      '-webkit-line-clamp': lines,
      '-webkit-box-orient': 'vertical',
    }),
  };
}

function useComputedLineHeight(lineHeightHelper: HTMLDivElement | null) {
  const onLoaded = useComponentLoaded('useComputedLineHeight');
  const [lineHeight, setLineHeight] = useState(0);

  const recalculateLineHeight = useCallback(() => {
    let height = 0;
    if (lineHeightHelper) {
      const computedStyles = getComputedStyle(lineHeightHelper);
      height = parseFloat(computedStyles.height);
    }
    setLineHeight(height);
    if (lineHeightHelper) onLoaded();
  }, [lineHeightHelper]);

  const size = useResizeObserver(lineHeightHelper);

  useEffect(() => recalculateLineHeight(), [size.height, recalculateLineHeight]);

  useEffect(() => {
    if (!lineHeightHelper) return;

    recalculateLineHeight();
    const listener = (change: Change) => {
      if (change.event === 'AdSizeChange') {
        recalculateLineHeight();
      }
    };

    changes.subscribe(listener);
    return () => changes.unsubscribe(listener);
  }, [lineHeightHelper, recalculateLineHeight]);

  return lineHeight;
}

function useLineClampStyle(
  lineHeightHelper: HTMLDivElement | null,
  { lines, dots, style }: Pick<LineClampProps, 'lines' | 'dots' | 'style'>
) {
  const lineHeight = useComputedLineHeight(lineHeightHelper);
  return useMemo(() => getLineClampStyle(lines, lineHeight, dots, style), [lines, lineHeight, dots, style]);
}

function useContentInserter(
  element: HTMLDivElement | undefined,
  { text, prefix, styleWord, keywords }: Pick<LineClampProps, 'text' | 'prefix' | 'styleWord' | 'keywords'>
) {
  const onTextInserted = useComponentLoaded('useContentInserter');

  useLayoutEffect(() => {
    if (!element) return;

    insertText(element, text, prefix, styleWord || null, keywords);
    onTextInserted();
  }, [element, text, prefix, styleWord, keywords]);
}

const HelperElementStyle = {
  position: 'absolute',
  top: -10000,
  left: 0,
  right: 0,
  whiteSpace: 'nowrap',
  wordBreak: 'keep-all',
};

export function LineClamp(props: LineClampProps) {
  const [container, setContainer] = useState<HTMLDivElement>();
  const [lineHeightHelper, setLineHeightHelper] = useState<HTMLDivElement | null>();
  const setRef = useCallback(
    (e: HTMLDivElement | null) => {
      if (e) {
        setContainer(e);
        if (props.containerRef) props.containerRef.current = e;
      }
    },
    [props.containerRef]
  );

  useContentInserter(container, props);
  useContentInserter(lineHeightHelper || undefined, props);

  const style = useLineClampStyle(lineHeightHelper || null, props);
  return (
    <div style={style} className={props.className} ref={setRef}>
      <div ref={setLineHeightHelper} style={HelperElementStyle} />
    </div>
  );
}
